import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Configure } from 'react-instantsearch';
import {
  ChipGroup, Grid, LinkAsButton,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { ErrorManagement } from '@printdeal/error-management/react';
import { type SearchClient } from 'algoliasearch';
import { INDICES } from '../../algolia-search/helper/algoliaHelper';
import {
  FilterFacets, getFilteredFacets, getIndex,
  getIndexFacets, getIndexSettings, mapAttributesForFaceting,
} from '../../algolia-search/helper/algoliaFacets';
import { FacetFilteringQuestion } from './FacetFilteringQuestion';
import { FacetFilteringProducts } from './FacetFilteringProducts';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { Questions } from '../../../services/product-ai/ProductAiService';
import { ServiceFactory } from '../../../services/ServiceFactory';

type FacetFilteringBannerProps = {
  category: string;
  pageName: string;
  client: SearchClient;
};

export type FilterState = {
  refinements: Refinement[];
  questionIndex: number;
  interacted: boolean;
  completed: boolean;
};

export type Refinement = {
  item: RefinementListItem;
  refine: (value: string) => void;
};

const MAX_HITS_PER_PAGE = 4;
const MAX_VALUES_PER_FACET = 100;

const productAiService = ServiceFactory.productAIService();

export const FacetFilteringBanner = ({ category, pageName, client }: FacetFilteringBannerProps) => {
  const intl = useIntl();
  const [facets, setFacets] = useState<string[]>();
  const [filteredFacets, setFilteredFacets] = useState<FilterFacets>({});
  const [filterState, setFilterState] = useState<FilterState>({
    refinements: [],
    questionIndex: 0,
    interacted: false,
    completed: false,
  });

  const { data, error } = useQuery<Questions>({
    queryKey: ['facetFilteringQuestions', filteredFacets],
    queryFn: () => productAiService.getFacetFilteringQuestions(filteredFacets),
    enabled: Object.keys(filteredFacets).length > 0,
  });

  const memoizedIndexFacets = useMemo(() => getIndexFacets(client, INDICES.PDP_INDEX, category), [client, category]);
  const indexSettings = useMemo(() => getIndexSettings(getIndex(client, INDICES.PDP_INDEX)), [client]);

  useEffect(() => {
    const fetchFacets = async () => {
      setFacets(mapAttributesForFaceting((await indexSettings).attributesForFaceting ?? []));
      setFilteredFacets(getFilteredFacets(await memoizedIndexFacets));
    };
    fetchFacets().catch((e) => {
      ErrorManagement.exception({
        exception: e,
        tags: {
          component: 'GuidedSellingAi',
          fileLocation: 'FacetFilteringBanner',
        },
        fingerprint: ['CategoryPage-FacetFiltering'],
      });
    });
  }, [client, category, memoizedIndexFacets, indexSettings]);

  if (error || !data || data.questions.length < 1) return null;

  const { questions } = data;

  const handleRefinement = (index: number, refinement?: Refinement) => {
    setFilterState((prev) => ({
      refinements: refinement ? [...prev.refinements, refinement] : [...prev.refinements],
      questionIndex: index < questions.length - 1 ? index + 1 : prev.questionIndex,
      completed: index >= questions.length - 1,
      interacted: true,
    }));
    dispatchDataLayerEntry(pageName, 'click_option_decision_assistance', {
      category,
      question: questions[index].question,
      answer: refinement ? refinement.item.label : '',
    });
  };

  const unsetRefinement = (refinement: Refinement) => {
    refinement.refine(refinement.item.value);
    setFilterState((prev) => {
      const newRefinements = prev.refinements.filter((f) => f !== refinement);
      return {
        refinements: newRefinements,
        questionIndex: newRefinements.length === 0 ? 0 : 1,
        completed: newRefinements.length !== 0,
        interacted: newRefinements.length !== 0,
      };
    });
  };

  const resetRefinements = () => {
    filterState.refinements.forEach((ref) => ref.refine(ref.item.value));
    setFilterState({
      refinements: [],
      questionIndex: 0,
      completed: false,
      interacted: false,
    });
  };

  const activeFiltersLabel = intl.formatMessage({
    id: 'general.chooseProduct.facetFiltering.label.activeFilters',
    defaultMessage: 'Filters',
  });

  return (
    <>
      <Configure
        filters={`productCategory.slug:${category}`}
        restrictSearchableAttributes={['productCategory.slug']}
        facets={facets}
        maxValuesPerFacet={MAX_VALUES_PER_FACET}
        hitsPerPage={MAX_HITS_PER_PAGE}
      />
      <Grid className="pd-gap-10">
        <Grid.Item className={clsx({ 'pd-hidden': filterState.completed }, 'pd-relative pd-flex pd-flex-row pd-items-center pd-gap-5 pd-rounded-md pd-bg-dim-100 pd-p-5')}>
          <p className="pd-text-dim-500">
            <FormattedMessage
              id="general.chooseProduct.facetFiltering.title"
              defaultMessage="Find your product"
            />
          </p>
          <span className="pd-h-12 pd-w-px pd-bg-dim-200" />
          <div className="pd-flex pd-flex-row pd-items-center pd-gap-4">
            <p className="pd-text-lg pd-font-bold">{questions[filterState.questionIndex].question}</p>
            {questions.map((question, index) => (
              <FacetFilteringQuestion
                index={index}
                question={question}
                questionIndex={filterState.questionIndex}
                callbackHandler={handleRefinement}
                filterState={filterState}
                key={question.question}
              />
            ))}
          </div>
        </Grid.Item>
        <Grid.Item as="hr" />
        {filterState.refinements.length > 0 && (
          <Grid.Item>
            <div className="pd-flex pd-flex-row pd-items-center pd-gap-5">
              <ChipGroup
                title={activeFiltersLabel}
                secondaryAction={(
                  <LinkAsButton
                    iconBefore="trashcan"
                    variant="secondary"
                    onClick={resetRefinements}
                    className="pd-text-sm"
                  >
                    <FormattedMessage
                      id="general.chooseProduct.facetFiltering.btn.startAgain"
                      defaultMessage="Start again"
                    />
                  </LinkAsButton>
                )}
              >
                {filterState.refinements.map((refinement) => (
                  <ChipGroup.Item
                    key={refinement.item.value}
                    onDismiss={() => unsetRefinement(refinement)}
                    dismissLabel={refinement.item.label}
                  >
                    {refinement.item.label}
                  </ChipGroup.Item>
                ))}
              </ChipGroup>
            </div>
          </Grid.Item>
        )}
        <Grid.Item>
          <FacetFilteringProducts />
        </Grid.Item>
      </Grid>
    </>
  );
};
